import { ApiError, type ApiErrorShape } from './types';

const TOKEN_KEY = 'mf_session_token';

export const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL ?? 'https://api.dotlineplan.com').replace(/\/$/, '');

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export interface RequestOptions extends Omit<RequestInit, 'body' | 'headers'> {
  body?: unknown;
  headers?: Record<string, string>;
  auth?: boolean;
  query?: Record<string, string | number | boolean | undefined | null>;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = path.startsWith('http') ? path : `${apiBaseUrl}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    qs.append(k, String(v));
  }
  const s = qs.toString();
  return s ? `${url}${url.includes('?') ? '&' : '?'}${s}` : url;
}

async function parseError(res: Response): Promise<ApiError> {
  let payload: ApiErrorShape | string | undefined;
  const text = await res.text().catch(() => '');
  if (text) {
    try {
      payload = JSON.parse(text) as ApiErrorShape;
    } catch {
      payload = text;
    }
  }
  const msg =
    (typeof payload === 'object' &&
      payload &&
      (payload.message ??
        (typeof payload.detail === 'string'
          ? payload.detail
          : payload.detail?.message))) ||
    (typeof payload === 'string' ? payload : '') ||
    `HTTP ${res.status}`;
  const code = typeof payload === 'object' && payload ? payload.code : undefined;
  return new ApiError(msg, res.status, code, payload);
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { body, headers = {}, auth = true, query, ...rest } = opts;
  const finalHeaders: Record<string, string> = { Accept: 'application/json', ...headers };
  let finalBody: BodyInit | undefined;

  if (body !== undefined && body !== null) {
    if (body instanceof FormData || body instanceof Blob || body instanceof ArrayBuffer) {
      finalBody = body as BodyInit;
    } else {
      finalHeaders['Content-Type'] = finalHeaders['Content-Type'] ?? 'application/json';
      finalBody = JSON.stringify(body);
    }
  }

  if (auth) {
    const t = getToken();
    if (t) finalHeaders.Authorization = `Bearer ${t}`;
  }

  const res = await fetch(buildUrl(path, query), { ...rest, headers: finalHeaders, body: finalBody });
  if (!res.ok) throw await parseError(res);

  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) return (await res.json()) as T;
  return (await res.text()) as unknown as T;
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'PATCH', body }),
  delete: <T>(path: string, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'DELETE' }),
};
